import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo } from 'react';

import type { ExtendedSeriesOptionsType } from '@/features/charts/types/highcharts';
import CurveComparisonSelector from '@/features/markets/components/Chart/ForwardCurveChartBlock/CurveComparisonSelector';
import { populateTemplates } from '@/features/markets/components/Chart/ForwardCurveChartBlock/populateTemplate';
import type { TemplateChartSeries } from '@/shared/types/charts';

const ButtonSection = ({
  chartSeries,
  templateChartSeries,
  comparisonTemplateSeries,
  setComparisonTemplateSeries
}: {
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
  templateChartSeries: TemplateChartSeries[] | undefined;
  comparisonTemplateSeries: TemplateChartSeries[];
  setComparisonTemplateSeries: Dispatch<SetStateAction<TemplateChartSeries[]>>;
}) => {
  const transactionDates = useMemo(
    () =>
      comparisonTemplateSeries
        .map(template => template.symbol.transactionDate)
        .filter((value): value is string => value !== undefined)
        .reduce<string[]>((acc, curr) => (acc.includes(curr) ? acc : [...acc, curr]), []),
    [comparisonTemplateSeries]
  );

  const parseTransactionDatesToTemplates = useCallback(
    (transactionDatesStrings: string[]) => {
      if (templateChartSeries === undefined) return;
      const templates = transactionDatesStrings.flatMap(transactionDate =>
        populateTemplates(transactionDate, templateChartSeries)
      );
      setComparisonTemplateSeries(templates);
    },
    [setComparisonTemplateSeries, templateChartSeries]
  );

  const populateTemplateSeriesWith = useCallback(
    (selectedDateString: string, comparisonSelectorIndex: number) => {
      const newTransactionDates = [...transactionDates];
      newTransactionDates[comparisonSelectorIndex] = selectedDateString;
      parseTransactionDatesToTemplates(newTransactionDates);
    },
    [parseTransactionDatesToTemplates, transactionDates]
  );

  const deleteFromTemplateSeriesWith = useCallback(
    (comparisonSelectorIndex: number) => {
      const newTransactionDates = transactionDates.filter(
        (_, index) => index !== comparisonSelectorIndex
      );
      parseTransactionDatesToTemplates(newTransactionDates);
    },
    [parseTransactionDatesToTemplates, transactionDates]
  );

  return (
    <div className='mt-4 flex flex-wrap gap-2'>
      <CurveComparisonSelector
        key='comparisonSelector1'
        chartSeries={chartSeries}
        comparisonSelectorIndex={0}
        deleteFromTemplateSeriesWith={deleteFromTemplateSeriesWith}
        label='Add date comparison'
        populateTemplateSeriesWith={populateTemplateSeriesWith}
        transactionDates={transactionDates}
      />
      {transactionDates.length > 0 && (
        <CurveComparisonSelector
          key='comparisonSelector2'
          chartSeries={chartSeries}
          comparisonSelectorIndex={1}
          deleteFromTemplateSeriesWith={deleteFromTemplateSeriesWith}
          label='Add date comparison'
          populateTemplateSeriesWith={populateTemplateSeriesWith}
          transactionDates={transactionDates}
        />
      )}
    </div>
  );
};
export default ButtonSection;
